import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BookTable from './BookTable';
import BookPagesTable from './BookPagesTable';
import Header from './Header';
import BookSelectorHero from './BookSelectorHero';
import { createBookDirectoryService } from '../services/BookDirectoryService';
import { settingsService } from '../services/SettingsService';

interface Book {
  isbn: string;
  book_title: string;
  publisher?: string;
  series?: string;
  in_all_books?: boolean;
  in_db?: boolean;
  page_count: number;
  fast_track: boolean;
  images_created: boolean;
  text_extracted_basic: boolean;
  text_extracted_blocked: boolean;
  text_extracted_enhanced: boolean;
  text_extracted?: string; // Optional - "basic", "blocked", "enhanced"
  text_extracted_combined?: boolean; // Optional
  text_extracted_files_prepared?: boolean; // Optional
  files_prepared_pages_processed?: number; // Optional
  possible_issues?: string; // Optional
  'ai-confidence'?: number; // Optional
  extracted_from?: string; // Optional - "pdf", etc.
  format?: string; // Optional - from all-books
  audio_inside?: boolean; // Optional - from all-books
  quiz_question_count?: string; // Optional - from DB lookup
  include_list_consumer?: boolean | null;
  include_list_educator?: boolean | null;
  good_audio?: boolean | null;
  approval?: {
    images?: boolean | string;
    words?: boolean | string;
    voice?: boolean | string;
  };
  voice_service?: string;
  voice_name?: string;
}

interface BookListData {
  books: Array<{
    isbn: string;
    book_title: string;
    publisher?: string;
    series?: string;
    in_all_books?: boolean;
    in_db?: boolean;
    page_count: number;
    fast_track: boolean;
    images_created: boolean;
    text_extracted_basic: boolean;
    text_extracted_combined: boolean;
    text_extracted_blocked: boolean;
    text_extracted_enhanced: boolean;
    text_extracted: string;
    text_extracted_files_prepared: boolean;
    files_prepared_pages_processed: number;
    possible_issues: string;
    'ai-confidence': number;
    extracted_from?: string;
    format?: string;
    audio_inside?: boolean;
    include_list_consumer?: boolean | null;
    include_list_educator?: boolean | null;
    good_audio?: boolean | null;
    approval?: {
      images?: boolean | string;
      words?: boolean | string;
      voice?: boolean | string;
    };
  }>;
}

const BookList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [books, setBooks] = useState<Book[]>([]);
  const [filteredBooks, setFilteredBooks] = useState<Book[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentView, setCurrentView] = useState<'info' | 'pages'>(() => 
    settingsService.getBookListView()
  );
  const [currentBookId, setCurrentBookId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFilters, setHasFilters] = useState(false);
  const [hasCustomColumnOrder, setHasCustomColumnOrder] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const clearFiltersRef = useRef<(() => void) | null>(null);
  // Info table filtered+sorted list and pagination (for pages table: same filters, order, first row)
  const [infoFilteredBooks, setInfoFilteredBooks] = useState<Book[]>([]);
  const [infoPageIndex, setInfoPageIndex] = useState(0);
  const [infoPageSize, setInfoPageSize] = useState(25);

  // Restore search query on mount
  useEffect(() => {
    const savedState = currentView === 'info' 
      ? settingsService.getInfoTableState()
      : settingsService.getPagesTableState();
    if (savedState?.searchQuery) {
      setSearchQuery(savedState.searchQuery);
    }
  }, [currentView]);

  // Save search query whenever it changes
  useEffect(() => {
    if (currentView === 'info') {
      const currentState = settingsService.getInfoTableState() || {
        filters: {},
        pagination: { pageIndex: 0, pageSize: 25 },
        searchQuery: '',
      };
      settingsService.setInfoTableState({
        ...currentState,
        searchQuery: searchQuery,
      });
    } else {
      const currentState = settingsService.getPagesTableState() || {
        filters: {},
        pagination: { pageIndex: 0, pageSize: 10 },
        searchQuery: '',
      };
      settingsService.setPagesTableState({
        ...currentState,
        searchQuery: searchQuery,
      });
    }
  }, [searchQuery, currentView]);

  // Save view state when it changes
  useEffect(() => {
    settingsService.setBookListView(currentView);
  }, [currentView]);

  // Define loadBookData first since autoLoadDefaultBookList depends on it
  const loadBookData = useCallback(async (bookListId: string) => {
    if (!bookListId) {
      setBooks([]);
      setFilteredBooks([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Import FolderService dynamically
      const { FolderService } = await import('../services/FolderService');
      const folder = FolderService.getFolder(); // Throws if folder is not set - NO FALLBACKS
      
      // Always load from API instead of localStorage
      const { authenticatedFetch } = await import('../utils/api');
      const url = FolderService.addFolderToUrl('/api/books/book-list');
      const response = await authenticatedFetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load book list: ${response.statusText}`);
      }
      
      let bookListData = await response.json();
      
      // Filter by selected list if not 'ALL'
      const { ListService } = await import('../services/ListService');
      const { settingsService } = await import('../services/SettingsService');
      const selectedList = settingsService.getSelectedList();
      
      if (selectedList && selectedList !== 'ALL') {
        try {
          const isbns = await ListService.getListContent(selectedList);
          const isbnSet = new Set(isbns);
          bookListData = {
            ...bookListData,
            books: bookListData.books.filter((book: any) => isbnSet.has(book.isbn))
          };
          console.log(`[BookList] loadBookData: Filtered to ${bookListData.books.length} books from list: ${selectedList}`);
        } catch (error) {
          console.error(`[BookList] Error filtering by list ${selectedList}:`, error);
          // Continue with all books if list filtering fails
        }
      }
      
      // Convert the book list format to our Book interface
      if (!bookListData.books || !Array.isArray(bookListData.books)) {
        throw new Error('Invalid book list data: missing or invalid books array');
      }
      
      const convertedBooks: Book[] = bookListData.books.map(book => ({
        isbn: book.isbn,
        book_title: book.book_title,
        publisher: book.publisher || '',
        series: book.series || '',
        in_all_books: book.in_all_books,
        in_db: book.in_db,
        page_count: book.page_count,
        file_type: book.file_type || '',
        audio_included: book.audio_included || '',
        audio_ai_ready: book.audio_ai_ready || '',
        quiz_question_count: book.quiz_question_count ?? '',
        'ai-confidence': typeof book['ai-confidence'] === 'number' ? book['ai-confidence'] : undefined,
        extracted_from: book.extracted_from || 'N/A',
        format: book.format || '',
        audio_inside: book.audio_inside,
        include_list_consumer: book.include_list_consumer,
        include_list_educator: book.include_list_educator,
        good_audio: book.good_audio,
        possible_issues: book.possible_issues || '',
        fast_track: book.fast_track || false,
        images_created: book.images_created || false,
        text_extracted_basic: book.text_extracted_basic === true || book.text_extracted_basic === 'true',
        text_extracted_combined: book.text_extracted_combined === true || book.text_extracted_combined === 'true',
        text_extracted_blocked: book.text_extracted_blocked === true || book.text_extracted_blocked === 'true',
        text_extracted_files_prepared: book.text_extracted_files_prepared || false,
        text_extracted_enhanced: book.text_extracted_enhanced === true || book.text_extracted_enhanced === 'true',
        text_extracted: book.text_extracted,
        files_prepared_pages_processed: book.files_prepared_pages_processed,
        approval: book.approval,
        production_file_sync_status: book.production_file_sync_status,
        voice_service: book.voice_service,
        voice_name: book.voice_name
      }));

      setBooks(convertedBooks);
      setFilteredBooks(convertedBooks);
    } catch (err) {
      console.error('Error loading book data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load book data');
      setBooks([]);
      setFilteredBooks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Define autoLoadDefaultBookList after loadBookData since it depends on it
  const autoLoadDefaultBookList = useCallback(async () => {
    try {
      // Import FolderService dynamically to avoid circular dependencies
      const { FolderService } = await import('../services/FolderService');
      // Sync folder from URL if missing (covers already-logged-in load where init didn't set it)
      FolderService.ensureFolderFromUrlIfUnset();
      const folder = FolderService.getFolderIfSet();
      
      if (!folder) {
        console.log('No folder selected, skipping auto-load');
        // Set error state so user sees the message
        setError('Please select a folder first');
        setLoading(false);
        return;
      }

      // Check if lists exist - if so, get the selected list
      const { ListService } = await import('../services/ListService');
      const { settingsService } = await import('../services/SettingsService');
      const availableLists = await ListService.getAvailableLists();
      const selectedList = settingsService.getSelectedList();
      
      // If lists exist but none selected, show all books by default
      // (Don't require selection - better UX to show something immediately)
      if (availableLists.length > 0 && !selectedList) {
        console.log('[BookList] Lists exist but none selected - showing all books by default');
      }
      
      // Load only the current list from API when needed (no caching of full list contents)
      const bookListId = folder;
      localStorage.setItem('current-book-list-id', bookListId);
      setCurrentBookId(bookListId);
      loadBookData(bookListId);
    } catch (error) {
      console.log('Not in S3 mode or default book list not available:', error);
      // Only show "select a folder" for real init/folder failures, not e.g. storage quota
      const isStorageQuota = error instanceof DOMException && error.name === 'QuotaExceededError';
      setError(isStorageQuota ? 'Storage quota exceeded. Free up space or use a different browser.' : 'Please select a folder first');
      setLoading(false);
    }
  }, [loadBookData]);

  // Restore table state when component mounts or when navigating back from book
  useEffect(() => {
    const savedState = currentView === 'info' 
      ? settingsService.getInfoTableState()
      : settingsService.getPagesTableState();
    
    if (savedState) {
      console.log('[BookList] Restoring saved table state on mount:', savedState);
      if (savedState.searchQuery) {
        setSearchQuery(savedState.searchQuery);
      }
    }

    // Load current book ID from localStorage
    const savedBookId = localStorage.getItem('current-book-list-id');
    if (savedBookId) {
      setCurrentBookId(savedBookId);
      loadBookData(savedBookId);
    } else {
      // Auto-load default book list in production/S3 mode
      // This will check for list selection requirement
      autoLoadDefaultBookList();
    }
  }, [currentView, loadBookData, autoLoadDefaultBookList]);

  // Listen for folder changes and list changes (both S3 and local mode)
  useEffect(() => {
    // Handle S3 folder changes - need to auto-load
    const handleS3FolderChange = () => {
      console.log('[BookList] S3 folder changed, refreshing book list...');
      // Clear current book ID to force reload
      setCurrentBookId(null);
      localStorage.removeItem('current-book-list-id');
      autoLoadDefaultBookList();
    };
    
    // Handle general folder changes - DON'T auto-load, let explicit selection handle it
    // This prevents race condition where autoLoadDefaultBookList overwrites the
    // explicitly selected book list ID
    const handleFolderChange = () => {
      console.log('[BookList] Folder changed (clearing state only, no auto-load)');
      // Only clear state, don't auto-load - the component that triggered
      // the folder change will handle loading the correct book list
      setCurrentBookId(null);
      setBooks([]);
      setFilteredBooks([]);
      localStorage.removeItem('current-book-list-id');
    };

    // Handle book list regeneration completed - reload data
    const handleBookListRegenerated = () => {
      console.log('[BookList] Book list regenerated, reloading data...');
      const savedBookId = localStorage.getItem('current-book-list-id');
      if (savedBookId) {
        loadBookData(savedBookId);
      } else {
        autoLoadDefaultBookList();
      }
    };

    const handleListChange = (event?: CustomEvent<{ list: string }>) => {
      const listValue = event?.detail?.list;
      console.log('[BookList] List changed, refreshing book list...', { listValue });
      // Clear current book ID and books to force reload
      setCurrentBookId(null);
      setBooks([]);
      setFilteredBooks([]);
      localStorage.removeItem('current-book-list-id');
      
      // If we have the list value from the event, ensure it's set in settings
      // This ensures autoLoadDefaultBookList can read it correctly
      if (listValue && settingsService.getSelectedList() !== listValue) {
        settingsService.setSelectedList(listValue);
      }
      
      autoLoadDefaultBookList();
    };

    window.addEventListener('s3FolderChanged', handleS3FolderChange);
    window.addEventListener('folderChanged', handleFolderChange);
    window.addEventListener('listChanged', handleListChange as EventListener);
    window.addEventListener('bookListRegenerated', handleBookListRegenerated);
    
    return () => {
      window.removeEventListener('s3FolderChanged', handleS3FolderChange);
      window.removeEventListener('folderChanged', handleFolderChange);
      window.removeEventListener('listChanged', handleListChange as EventListener);
      window.removeEventListener('bookListRegenerated', handleBookListRegenerated);
    };
  }, [autoLoadDefaultBookList]);

  const handleBookFileSelected = useCallback(async (bookListId: string, bookDirectoryPath?: string) => {
    setCurrentBookId(bookListId);
    localStorage.setItem('current-book-list-id', bookListId);

    if (bookListId && bookDirectoryPath) {
      try {
        const bookDirectoryService = createBookDirectoryService();
        await bookDirectoryService.configureFromBookList(bookListId, `Book List ${bookListId}`, bookDirectoryPath);
      } catch (error) {
        console.error('Error configuring book directory:', error);
        alert(`Error configuring book directory: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    loadBookData(bookListId);
  }, [loadBookData]);

  const handleBookClick = useCallback((isbn: string) => {
    // Preserve books-to-edit parameter when navigating to book
    const currentParams = new URLSearchParams(window.location.search);
    const booksToEdit = currentParams.get('books-to-edit');
    const url = booksToEdit ? `/book/${isbn}?books-to-edit=${encodeURIComponent(booksToEdit)}` : `/book/${isbn}`;
    navigate(url);
  }, [navigate]);

  const handleClearAllFilters = useCallback(() => {
    // Clear search
    setSearchQuery('');
    // Clear table filters and reset pagination (only in info view)
    if (currentView === 'info' && clearFiltersRef.current) {
      clearFiltersRef.current();
      setCurrentPageIndex(0);
    }
  }, [currentView]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleViewChange = useCallback((view: 'info' | 'pages') => {
    setCurrentView(view);
  }, []);

  const handleFilteredSortedStateChange = useCallback((books: Book[], pageIndex: number, pageSize: number) => {
    setInfoFilteredBooks(books);
    setInfoPageIndex(pageIndex);
    setInfoPageSize(pageSize);
  }, []);

  const handleSyncCompleted = useCallback((updates: Array<{ isbn: string; approval: { images?: string; words?: string } }>) => {
    if (updates.length === 0) return;
    setBooks((prev) => {
      const isbnToUpdate = new Map(updates.map((u) => [u.isbn, u.approval]));
      return prev.map((b) => {
        const approvalUpdate = isbnToUpdate.get(b.isbn);
        if (!approvalUpdate) return b;
        return {
          ...b,
          approval: { ...b.approval, ...approvalUpdate },
        };
      });
    });
  }, []);

  const handleHeroDirectorySelect = () => {
    // Trigger directory selection by calling the callback
    // This will be handled by the BookFileSelector component
    const event = new CustomEvent('triggerDirectorySelect');
    window.dispatchEvent(event);
  };


  const handleHeroPastePath = () => {
    // Trigger path paste by calling the callback
    // This will be handled by the BookFileSelector component
    const event = new CustomEvent('triggerPathPaste');
    window.dispatchEvent(event);
  };

  // Filter books based on search term
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredBooks(books);
    } else {
      const filtered = books.filter(book =>
        book.book_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        book.isbn.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredBooks(filtered);
    }
  }, [books, searchQuery]);

  // Memoize books with stable reference - only create new array if content actually changed
  const memoizedBooks = useMemo(() => {
    // Create a stable key from books to detect actual changes
    const booksKey = filteredBooks.map(b => b.isbn).sort().join(',') + `|${filteredBooks.length}`;
    return filteredBooks;
  }, [filteredBooks]);

  if (loading) {
    return (
      <div className="app-container">
        <Header 
          currentView={currentView}
          onViewChange={handleViewChange}
          onSearchChange={handleSearchChange}
          searchQuery={searchQuery}
          onBookFileSelected={handleBookFileSelected}
          currentBookPath={currentBookId}
          hasFilters={hasFilters}
          isNotOnFirstPage={currentPageIndex > 0}
          onClearAllFilters={handleClearAllFilters}
        />
        <div className="book-list-container">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div>Loading books...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const handleShowAll = async () => {
      // Set selected list to "ALL" (same as selecting --ALL-- from dropdown)
      settingsService.setSelectedList('ALL');
      
      // Update URL parameter
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('list', 'ALL');
        return newParams;
      });
      
      // Clear error and reload data
      setError(null);
      // Get folder from FolderService - will throw if not set
      const { FolderService } = await import('../services/FolderService');
      const folder = FolderService.getFolder();
      setCurrentBookId(folder);
      loadBookData(folder);
    };

    return (
      <div className="app-container">
        <Header 
          currentView={currentView}
          onViewChange={handleViewChange}
          onSearchChange={handleSearchChange}
          searchQuery={searchQuery}
          onBookFileSelected={handleBookFileSelected}
          currentBookPath={currentBookId}
          hasFilters={hasFilters}
          isNotOnFirstPage={currentPageIndex > 0}
          onClearAllFilters={handleClearAllFilters}
        />
        <div className="book-list-container">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{ color: 'var(--text-error)' }}>Error: {error}</div>
            <button 
              onClick={handleShowAll}
              style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}
            >
              Show All
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Header 
        currentView={currentView}
        onViewChange={handleViewChange}
        onSearchChange={handleSearchChange}
        searchQuery={searchQuery}
        onBookFileSelected={handleBookFileSelected}
        currentBookPath={currentBookId}
        hasFilters={hasFilters || hasCustomColumnOrder}
        isNotOnFirstPage={currentPageIndex > 0}
        onClearAllFilters={handleClearAllFilters}
      />
      <div className="book-list-container">
        {memoizedBooks.length === 0 ? (
          <BookSelectorHero />
        ) : (
          <>
            {currentView === 'info' ? (
              <BookTable 
                books={memoizedBooks} 
                onOpenBook={handleBookClick}
                onFiltersChange={setHasFilters}
                onPaginationChange={setCurrentPageIndex}
                onColumnOrderChange={setHasCustomColumnOrder}
                onFilteredSortedStateChange={handleFilteredSortedStateChange}
                clearFiltersRef={clearFiltersRef}
                currentFolder={currentBookId}
                onSyncCompleted={handleSyncCompleted}
              />
            ) : (
              <BookPagesTable 
                books={infoFilteredBooks.length > 0 ? infoFilteredBooks : memoizedBooks}
                onOpenBook={handleBookClick}
                initialFirstRowIndex={infoFilteredBooks.length > 0 ? infoPageIndex * infoPageSize : undefined}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BookList;